import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";
import { services } from "@/data/services";

export default function ServicesHub() {
  const [featured, ...rest] = services;
  const FeaturedIcon = Icons[featured.icon] || Icons.ShieldCheck;
  return (
    <PageLayout
      seoTitle="Cybersecurity Services & Consulting | Aliwer"
      seoDescription="Explore Aliwer's cybersecurity services: assessments, penetration testing, application security, threat modeling, cloud, AI security, compliance, managed security, and vCISO."
      path="/services"
      eyebrow="Services"
      title="Security services, engineered for outcomes."
      subtitle="From point-in-time assessments to continuous managed security and fractional leadership — meet us anywhere on the Assess → Design → Protect → Monitor → Prove journey."
      testid="services-hub"
    >
      {/* Featured practice */}
      <Reveal>
        <Link
          to={`/services/${featured.slug}`}
          data-testid={`service-card-${featured.slug}`}
          className="group grid md:grid-cols-[1.4fr_1fr] gap-8 rounded-2xl border border-white/15 bg-surface p-8 md:p-10 mb-10 hover:bg-[#232326] transition-colors duration-500"
        >
          <div>
            <span className="inline-block text-[10px] uppercase tracking-widest border border-white/25 text-paper rounded-full px-2.5 py-1 mb-5">
              Start here
            </span>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-paper mb-3">{featured.title}</h2>
            <p className="text-sm text-fog leading-relaxed max-w-xl">{featured.card}</p>
            <span className="mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-paper">
              Learn more
              <Icons.ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
          <div className="hidden md:flex flex-col items-end justify-between">
            <FeaturedIcon size={44} className="text-paper/80" strokeWidth={1} />
            <p className="text-xs text-fog/80 text-right leading-relaxed max-w-[26ch]">
              Most engagements begin here — the findings drive everything that follows.
            </p>
          </div>
        </Link>
      </Reveal>

      {/* Practice list */}
      <div className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/10" data-testid="services-list">
        {rest.map((s, i) => {
          const Icon = Icons[s.icon] || Icons.ShieldCheck;
          return (
            <Reveal key={s.slug} delay={i * 0.02}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group grid grid-cols-[2.5rem_1fr_auto] sm:grid-cols-[3rem_1.2fr_2fr_auto] items-center gap-4 sm:gap-6 px-6 sm:px-8 py-5 bg-black/40 hover:bg-surface transition-colors duration-300"
              >
                <span className="font-display text-xl tracking-tighter text-fog/40">
                  {String(i + 2).padStart(2, "0")}
                </span>
                <span className="flex items-center gap-3">
                  <Icon size={17} className="text-paper shrink-0" strokeWidth={1.4} />
                  <span className="font-display text-base sm:text-lg tracking-tight text-paper">{s.title}</span>
                </span>
                <span className="hidden sm:block text-sm text-fog leading-relaxed line-clamp-2">{s.card}</span>
                <Icons.ArrowUpRight
                  size={16}
                  className="text-fog justify-self-end transition-all duration-300 group-hover:text-paper group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </Link>
            </Reveal>
          );
        })}
      </div>
    </PageLayout>
  );
}
